import { Component } from '@theme/component';
import { setCustomProperty } from '@theme/utilities';

const MS_PER_MINUTE = 60_000;
const MS_PER_DAY = 86_400_000;

/**
 * A slide's daily time-of-day window, in minutes since midnight (0-1439), store time zone.
 * Both null means "evergreen" — eligible any time nothing time-limited is currently due.
 *
 * @typedef {object} ScheduleSlot
 * @property {number | null} startMin
 * @property {number | null} endMin
 */

/**
 * @typedef {object} Window
 * @property {number} startMs - Start offset (ms) since midnight.
 * @property {number} endMsExclusive - End offset (ms) since midnight, exclusive.
 * @property {boolean} wraps - True when the window crosses midnight into the next day.
 */

/**
 * @typedef {object} Refs
 * @property {HTMLElement[]} [slides]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #controller = new AbortController();

  /** Handle for the pending schedule tick. @type {number|undefined} */
  #tickTimeoutId;

  /** Index into `refs.slides` / `#schedule` currently shown. -1 = nothing shown yet. */
  #current = -1;

  /** @type {ScheduleSlot[]} */
  #schedule = [];

  /** "Today's" midnight (store time zone) at page-render time, as an epoch ms. @type {number} */
  #dayAnchorMs = 0;

  #hovered = false;

  connectedCallback() {
    super.connectedCallback();

    this.#dayAnchorMs = Number(this.dataset.dayAnchorMs) || 0;
    this.#buildSchedule();

    if (this.#isRotating) {
      const { signal } = this.#controller;
      this.addEventListener('mouseenter', this.suspend, { signal });
      this.addEventListener('mouseleave', this.resume, { signal });
      document.addEventListener('visibilitychange', this.#handleVisibilityChange, { signal });
    }

    // Resolve + paint the slide that's actually due right now before anything else happens,
    // so there's no visible hop from the server-rendered default to the scheduled one.
    this.#sync();
    this.setAttribute('ready', '');

    if (this.#isRotating) this.#scheduleNextTick();
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.suspend();
  }

  get #isRotating() {
    return this.#schedule.length > 1;
  }

  /** Pauses schedule advancement while the pointer is hovering the bar (reading time). */
  suspend = () => {
    this.#hovered = true;
    clearTimeout(this.#tickTimeoutId);
  };

  /** Resumes schedule advancement, snapping back to whatever is truly due right now. */
  resume = () => {
    this.#hovered = false;
    if (!this.#isRotating) return;

    this.#sync();
    this.#scheduleNextTick();
  };

  /**
   * Reads each slide's `data-start-min` / `data-end-min` (minutes since midnight, store time
   * zone) in document order.
   */
  #buildSchedule() {
    const slides = this.refs.slides ?? [];

    this.#schedule = slides.map((slide) => ({
      startMin: parseMinutes(slide.dataset.startMin),
      endMin: parseMinutes(slide.dataset.endMin),
    }));
  }

  /**
   * "Today's" midnight, projected forward/backward from the page-render-time anchor by whole
   * days. Handles a tab staying open across one or more midnights using plain day arithmetic —
   * no knowledge of the store's UTC offset needed beyond the one anchor point already given.
   *
   * @param {number} now
   * @returns {number}
   */
  #currentDayMidnightMs(now) {
    if (!this.#dayAnchorMs) return now - (now % MS_PER_DAY);

    const dayIndex = Math.floor((now - this.#dayAnchorMs) / MS_PER_DAY);
    return this.#dayAnchorMs + dayIndex * MS_PER_DAY;
  }

  /**
   * Figures out which slide owns this exact moment, purely from the wall clock. Called fresh
   * on every tick/resume instead of decrementing a counter, so nothing ever drifts — a
   * throttled background tab just skips ticks and self-corrects the instant it's checked again.
   *
   * Priority: a slide with a time window that currently contains `now` wins. If none does, fall
   * back to the first "evergreen" slide (no time limit at all) so there's always something
   * sensible to show during gaps in the schedule. If even that doesn't exist, show the first
   * slide regardless — the bar must never end up with nothing eligible.
   *
   * @param {number} [now]
   * @returns {{ index: number, remainingMs: number | null }}
   */
  #resolve(now = Date.now()) {
    if (this.#schedule.length === 0) return { index: -1, remainingMs: null };

    const msSinceMidnight = now - this.#currentDayMidnightMs(now);
    const isTimeLimited = (/** @type {ScheduleSlot} */ slot) => slot.startMin != null && slot.endMin != null;
    const isDueNow = (/** @type {ScheduleSlot} */ slot) =>
      isTimeLimited(slot) && isWithinWindow(msSinceMidnight, toWindow(slot));

    let index = this.#schedule.findIndex(isDueNow);
    if (index === -1) index = this.#schedule.findIndex((slot) => !isTimeLimited(slot));
    if (index === -1) index = 0;

    const slot = this.#schedule[index];
    const remainingMs = isTimeLimited(slot) ? remainingUntilEnd(msSinceMidnight, toWindow(slot)) : null;

    return { index, remainingMs };
  }

  /** Snaps the visible slide + countdown to the truth of the wall clock. */
  #sync() {
    const { index, remainingMs } = this.#resolve();
    if (index === -1) return;

    this.#show(index);
    this.#renderCountdown(remainingMs);
  }

  /** Re-checks on each real-second boundary — cheap, and immune to setInterval drift. */
  #scheduleNextTick() {
    clearTimeout(this.#tickTimeoutId);

    const msToNextSecond = 1000 - (Date.now() % 1000);
    this.#tickTimeoutId = setTimeout(() => {
      if (this.#hovered || document.hidden) return;

      const { index, remainingMs } = this.#resolve();
      if (index !== -1 && index !== this.#current) this.#show(index);
      this.#renderCountdown(remainingMs);
      this.#scheduleNextTick();
    }, msToNextSecond);
  }

  /**
   * @param {number} index
   */
  #show(index) {
    const slides = this.refs.slides ?? [];
    const changed = index !== this.#current;
    const activeSlide = slides[index];

    this.#current = index;

    // `#show` also runs on resume (pointer leaving the bar, tab regaining focus), which is far
    // more often than the due slide actually changes. Re-setting an attribute to the value it
    // already holds still queues a mutation record and re-runs the [aria-hidden] transitions, so
    // each write is gated on a real change.
    slides.forEach((slide, i) => {
      const hidden = `${i !== index}`;
      if (slide.getAttribute('aria-hidden') !== hidden) slide.setAttribute('aria-hidden', hidden);
    });

    // The active block's own background_color colors the whole bar, not just its text — set on
    // the outer <aside> (not this component) so the color fills behind its padding too, with no
    // unpainted strip along the top/bottom edges. Only one slide is ever visible at a time, so
    // one property on a shared ancestor is enough.
    const bar = /** @type {HTMLElement | null} */ (this.closest('.announcement-bar'));
    const bgColor = activeSlide?.dataset.bgColor;
    if (bar) {
      if (bgColor) {
        setCustomProperty(bar, '--announcement-active-bg', bgColor);
      } else {
        bar.style.removeProperty('--announcement-active-bg');
      }
    }

    if (changed) this.#trackView(activeSlide);
  }

  /**
   * @param {number | null} remainingMs - Null when the current slide isn't time-limited (nothing
   * to count down to); the badge markup doesn't exist in that case, so this is a no-op.
   */
  #renderCountdown(remainingMs) {
    const slide = (this.refs.slides ?? [])[this.#current];
    const countdown = slide?.querySelector('[data-countdown]');
    if (!countdown || remainingMs == null) return;

    const { hours, minutes, seconds } = splitCountdown(remainingMs);

    // Assigning textContent always tears down the old text node and builds a new one, so writing
    // all three boxes every tick churned the DOM three times a second on every page — and two of
    // those three were re-writing digits that had not moved (hours and minutes only change once
    // an hour / once a minute). That churn is what `Component`'s subtree MutationObserver picks
    // up, and what makes the countdown flicker continuously in DevTools while nothing is
    // happening. Only the box whose digits actually changed is touched now.
    setText(countdown.querySelector('[data-countdown-hours]'), hours);
    setText(countdown.querySelector('[data-countdown-minutes]'), minutes);
    setText(countdown.querySelector('[data-countdown-seconds]'), seconds);
  }

  /**
   * Pauses schedule advancement while the tab is hidden — no point re-computing/repainting
   * something nobody can see, and it keeps the visible countdown from jumping ahead on return.
   */
  #handleVisibilityChange = () => (document.hidden ? this.suspend() : this.resume());

  /**
   * Reports a click on an announcement link. Fires three ways so it's usable regardless of
   * what's installed on the store: a DOM event other theme/app code can hook into, a
   * `dataLayer` push for GTM/GA4, and Shopify's own `analytics.publish` for Web Pixels
   * (Shopify Analytics only surfaces it if a pixel is subscribed to listen for it).
   *
   * `sourcePage` (the page the click happened on — see data-source-page in
   * header-announcements.liquid) travels as an event parameter, not a URL param: it never
   * touches `link.href`, so it can't affect GA4's landing-page/session attribution the way
   * appending real `utm_*` params to this internal link would.
   *
   * @param {Event} event
   */
  trackClick(event) {
    const link = event.target;
    if (!(link instanceof HTMLAnchorElement)) return;

    const slide = link.closest('[data-block-id]');
    const detail = {
      blockId: slide?.getAttribute('data-block-id') ?? '',
      index: this.#current,
      href: link.href,
      text: link.textContent?.trim().slice(0, 140) ?? '',
      sourcePage: this.dataset.sourcePage ?? '',
    };

    publishAnalytics('click', 'announcement_bar_click', detail);
  }

  /**
   * @param {HTMLElement} [slide]
   */
  #trackView(slide) {
    if (!slide) return;

    publishAnalytics('view', 'announcement_bar_view', {
      blockId: slide.getAttribute('data-block-id') ?? '',
      index: this.#current,
      sourcePage: this.dataset.sourcePage ?? '',
    });
  }
}

/**
 * Writes text into a countdown box only when the digits differ from what is on screen.
 *
 * @param {Element | null} element - The countdown box, if it is rendered at all.
 * @param {string} text - The two-digit value it should display.
 */
function setText(element, text) {
  if (element && element.textContent !== text) element.textContent = text;
}

/**
 * @param {string | undefined} value - Raw `data-start-min`/`data-end-min` attribute value.
 * @returns {number | null}
 */
function parseMinutes(value) {
  if (value == null || value === '') return null;

  const minutes = Number(value);
  return Number.isFinite(minutes) && minutes >= 0 && minutes < 1440 ? minutes : null;
}

/**
 * Turns a slot's Hour:Minute bounds into a millisecond window. "00:00" as an end time means
 * "end of day" (the natural reading of a range like "21:01–00:00"), not "minute 0, inclusive" —
 * every other end time stays active through that whole minute (e.g. "18:59" covers up to,
 * but not including, 19:00).
 *
 * @param {ScheduleSlot} slot
 * @returns {Window}
 */
function toWindow(slot) {
  const startMs = (slot.startMin ?? 0) * MS_PER_MINUTE;
  const endMin = slot.endMin ?? 0;
  const endExclusiveMin = endMin === 0 ? 1440 : endMin + 1;
  const endMsExclusive = endExclusiveMin * MS_PER_MINUTE;

  return { startMs, endMsExclusive, wraps: endMsExclusive <= startMs };
}

/**
 * @param {number} msSinceMidnight
 * @param {Window} window
 * @returns {boolean}
 */
function isWithinWindow(msSinceMidnight, window) {
  const { startMs, endMsExclusive, wraps } = window;

  return wraps
    ? msSinceMidnight >= startMs || msSinceMidnight < endMsExclusive
    : msSinceMidnight >= startMs && msSinceMidnight < endMsExclusive;
}

/**
 * @param {number} msSinceMidnight
 * @param {Window} window
 * @returns {number}
 */
function remainingUntilEnd(msSinceMidnight, window) {
  const { startMs, endMsExclusive, wraps } = window;

  if (!wraps) return endMsExclusive - msSinceMidnight;
  if (msSinceMidnight >= startMs) return MS_PER_DAY - msSinceMidnight + endMsExclusive;

  return endMsExclusive - msSinceMidnight;
}

/**
 * @param {number} remainingMs
 * @returns {{ hours: string, minutes: string, seconds: string }}
 */
function splitCountdown(remainingMs) {
  const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
  const pad = (/** @type {number} */ value) => String(value).padStart(2, '0');

  return {
    hours: pad(Math.floor(totalSeconds / 3600)),
    minutes: pad(Math.floor((totalSeconds % 3600) / 60)),
    seconds: pad(totalSeconds % 60),
  };
}

/**
 * @param {string} domEventSuffix - Suffix for the `announcement-bar:*` DOM CustomEvent.
 * @param {string} analyticsEventName - Event name published to dataLayer / Shopify analytics.
 * @param {Record<string, unknown>} detail
 */
function publishAnalytics(domEventSuffix, analyticsEventName, detail) {
  document.dispatchEvent(new CustomEvent(`announcement-bar:${domEventSuffix}`, { detail, bubbles: true }));

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: analyticsEventName, ...detail });

  try {
    window.Shopify?.analytics?.publish?.(analyticsEventName, detail);
  } catch {
    // Analytics must never break navigation or rotation.
  }
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
